import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'

// TODO: get rid of isDirect and replace with type
// TODO: Rename FaFunction to IdcFunction

/**
 * Contains various types related to {@link FaFunction.Category IDC categories}
 * and {@link FaFunction.Subcategory subcategories}
 */
export namespace FaFunction {
  /**
   * IDC stands for "Facilities and Administration." Each
   * {@link Category IDC category} is either a {@link Type.Direct direct}
   * category, meaning it corresponds to a primary function of an institution
   * like teaching students or conducting research, or it is an indirect category,
   * meaning it coresponds to an auxiliary function like maintaining buildings
   * and hiring employees. Indirect categories are either related to the cost of
   * having, using, and maintaining {@link Type.Facilities facilities} or they
   * are related to {@link Type.Administration administrative} overhead tasks.
   */
  export enum Type {
    /**
     * Costs associated with having, using, or maintaining physical space or
     * equipment
     */
    Facilities = 'facilities',
    /**
     * Costs associated with overhead tasks needed to ensure the institution
     * continues to run smoothly, such as HR, compliance, accounting, etc.
     */
    Administration = 'administration',
    /**
     * Costs associated with the main goals of an institution, such as teaching
     * students, conducting research, and in some cases helping the community,
     * etc.
     */
    Direct = 'direct',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)

    /**
     * Returns the {@link core.common.value.types.Enum.Entry} for the given
     * {@link Type}
     */
    export function getEntry(type: Type): core.common.value.types.Enum.Entry {
      return { ...ENTRY_LOOKUP[type], id: type }
    }
    /** Used by {@link getEntry} */
    const ENTRY_LOOKUP: Record<
      Type,
      Omit<core.common.value.types.Enum.Entry, 'id'>
    > = {
      [Type.Administration]: {
        description: 'Indirect IDC functions pertaining to administrative work',
        name: 'Indirect - Administration',
      },
      [Type.Direct]: {
        description:
          "Direct IDC functions pertaining to the insitution's mission",
        name: 'Direct',
      },
      [Type.Facilities]: {
        description:
          'Indirect IDC functions pertaining to facility acquisition and management',
        name: 'Indirect - Facilities',
      },
    }

    /**
     * Any {@link Type} that is not {@link Type.Direct}
     */
    export type OfIndirect = Exclude<Type, Type.Direct>
    export namespace OfIndirect {
      export const isOfIndirect = TypeChecker.Factory.exclude(
        isType,
        TypeChecker.Factory.exact(Type.Direct),
      )
      export const assertIsOfIndirect: TypeAsserter<OfIndirect> =
        TypeAsserter.Factory.from(isOfIndirect)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export const ALL = Type.ALL.filter(isOfIndirect)
    }
  }

  type CategoryBase = core.common.Identified<Category.Id> &
    core.common.Coded &
    core.common.Named
  /**
   * Represents a category of expenses in the context of an IDC rate proposal
   *
   * The government specifies a bunch of {@link Category.Standard standard}
   * categories, each with their own allocation methodolog(y/ies), but there could also be some custom ones
   */
  export interface Category extends CategoryBase {
    /** @see {@link Type} */
    type: Type
  }
  export namespace Category {
    export const isCategory: TypeChecker<Category> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          core.common.Identified.getTypeChecker(Id.isId),
          core.common.Named.isNamed,
          core.common.Coded.isCoded,
          core.common.Positioned.isPositioned,
          TypeChecker.Factory.object<Difference<Category, CategoryBase>>({
            type: Type.isType,
          }),
        ),
    ).usingAlias('FaFunction.Category')

    // NOTE: must match type for dms.data.Record.Id (but cannot import due to
    // circular dependencies)
    export type Id = PartialId
    export namespace Id {
      export const isId: TypeChecker<Id> = PartialId.isPartialId.usingAlias(
        'FaFunction.Category.Id',
      )
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)

      /**
       * {@link Category.Id} from a {@link Categorization.Identifiable}
       */
      export function from(
        identifier: Categorization.Identifiable,
      ): Id | undefined {
        return Categorization.Ids.isIds(identifier)
          ? identifier.categoryId
          : identifier.category.id
      }
    }

    /**
     * There are a number of standard {@link Category} that are used in the
     * system, especially in Rate and somewhat Space
     */
    export namespace Standard {
      /**
       * ID of a {@link Standard} {@link Category}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Id<TCode extends Code = Code> = Satisfies<
        PartialId.Standard<TCode>,
        Category.Id
      >
      export namespace Id {
        export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
          PartialId.Standard.getTypeChecker(Code.isCode),
        )
        export const assertIsId: TypeAsserter<Id> =
          TypeAsserter.Factory.from(isId)

        /**
         * {@link Category.Standard.Id} from the name of a
         * {@link Category.Standard.Code}. Similar to {@link Id.from}, but
         * avoids long references to {@link Category.Standard.Code}
         *
         * @see {@link Id.from}
         */
        export function of<K extends StandardCategoryCodeKey>(
          key: K,
        ): Id<(typeof Code)[K]> {
          return from(Code[key])
        }

        /**
         * {@link Category.Standard.Id} from {@link Category.Standard.Code}
         *
         * @see {@link Code.from}
         */
        export function from<TCode extends Code>(code: TCode): Id<TCode> {
          return PartialId.Standard.create(code)
        }
      }

      /**
       * Code of a {@link Standard} {@link Category}
       */
      export enum Code {
        // NOTE: order matters for `Position` below
        BuildingDepreciation = 'BLDG',
        EquipmentDepreciation = 'EQUIP',
        Interest = 'INT',
        OperationsAndMaintenance = 'OM',
        GeneralAdministration = 'GA',
        DepartmentAdministration = 'DA',
        SponsoredProjectsAdministration = 'SPA',
        Library = 'LIB',
        // TODO: rename to StudentAdministrativeServices
        StudentAdministrationAndServices = 'SAS',
        Instruction = 'INST',
        OrganizedResearch = 'OR',
        OtherSponsoredActivities = 'OSA',
        OtherInstitutionalActivities = 'OIA',
        CollegeAdministration = 'CA',
      }
      // Needs to be before namespace is defined
      const CODES: ReadonlySet<Code> = new Set(Object.values(Code) as Code[])
      export namespace Code {
        export const ALL: readonly Code[] = Array.from(CODES)
        export const isCode = TypeChecker.Factory.oneOf(CODES)
        export const assertIsCode: TypeAsserter<Code> =
          TypeAsserter.Factory.from(isCode)

        /**
         * {@link Category.Standard.Code} from {@link Category.Standard.Id}
         *
         * @see {@link Id.from}
         */
        export function from(id: Id): Code {
          return PartialId.getSuffix(id) satisfies `${Code}` as Code
        }
      }

      export namespace Position {
        /**
         * Retrieves the position of a {@link Standard} {@link Category}
         */
        export function from(idOrCode: Id | Code): number {
          return Code.ALL.indexOf(
            Code.isCode(idOrCode) ? idOrCode : Code.from(idOrCode),
          )
        }
      }
    }

    /**
     * Used to filter {@link Category categories}
     */
    export interface Filters {
      ids?: readonly Id[]
      types?: readonly Type[]
    }
    export namespace Filters {
      export const isFilters: TypeChecker<Filters> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.object<Filters>({
            ids: TypeChecker.Factory.optional(
              TypeChecker.Factory.list(Id.isId),
            ),
            types: TypeChecker.Factory.optional(
              TypeChecker.Factory.list(Type.isType),
            ),
          }),
      ).usingAlias('FaFunction.Category.Filters')
    }

    /**
     * A {@link Category}, potentially with a list of
     * {@link Subcategory subcategories}
     */
    export interface WithSubcategories extends Category {
      subcategories?: Subcategory[]
    }
    export namespace WithSubcategories {
      export const isWithSubcategories: TypeChecker<WithSubcategories> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.intersection(
            isCategory,
            TypeChecker.Factory.object<Omit<WithSubcategories, keyof Category>>(
              {
                subcategories: TypeChecker.Factory.lazy(() =>
                  TypeChecker.Factory.optional(
                    TypeChecker.Factory.list(Subcategory.isSubcategory),
                  ),
                ),
              },
            ),
          ),
        ).usingAlias('FaFunction.Category.WithSubcategories')
    }
  }

  type SubcategoryBase = core.common.Identified<Subcategory.Id> &
    core.common.Coded &
    core.common.Named
  /**
   * Some {@link Category IDC categories} are split into parts, usually so that
   * certain costs can be allocated differently in a Rate proposal.
   *
   * Specifically, most subcategories exist either because they have a specific
   * allocation methodology (e.g. OM_EHS only considers rooms that are Lab or
   * Lab Service) or because they allow users to define specific statistics for
   * specific subcategories (e.g. OM building subcategories or any Library
   * subcategories).
   */
  export interface Subcategory extends SubcategoryBase {
    isUtilityRelated: boolean
  }
  export namespace Subcategory {
    export const isSubcategory: TypeChecker<Subcategory> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.intersection(
          core.common.Identified.getTypeChecker(Id.isId),
          core.common.Named.isNamed,
          core.common.Coded.isCoded,
          TypeChecker.Factory.object<Difference<Subcategory, SubcategoryBase>>({
            isUtilityRelated: TypeChecker.isBoolean,
          }),
        ),
      ).usingAlias('FaFunction.Subcategory')

    // NOTE: must match type for dms.data.Record.Id (but cannot import due to
    // circular dependencies)
    export type Id = PartialId
    export namespace Id {
      export const isId: TypeChecker<Id> = PartialId.isPartialId.usingAlias(
        'FaFunction.Subcategory.Id',
      )
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)

      /**
       * {@link Category.Id} from a {@link Categorization.Identifiable}
       */
      export function from(
        identifier: Categorization.Identifiable,
      ): Id | undefined {
        return Categorization.Ids.isIds(identifier)
          ? identifier.subcategoryId
          : identifier.subcategory?.id
      }
    }

    /**
     * There are a number of standard {@link Subcategory} that are used in the
     * system, especially in Rate and somewhat Space
     */
    export namespace Standard {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Id<
        TCategoryCode extends Category.Standard.Code = Category.Standard.Code,
        TSubcategoryCode extends
          Subcategory.Standard.Code = Subcategory.Standard.Code,
      > = PartialId.Standard<Id.Suffix<TCategoryCode, TSubcategoryCode>>
      export namespace Id {
        export const isId: TypeChecker<Id> = PartialId.Standard.getTypeChecker(
          TypeChecker.Factory.lazy(() => Suffix.isSuffix),
        ).usingAlias('FaFunction.Subcategory.Id')
        export const assertIsId: TypeAsserter<Id> =
          TypeAsserter.Factory.from(isId)

        /**
         * Combines a {@link Category.Standard.Code} and
         * {@link Subcategory.Standard.Code}
         */
        export type Suffix<
          TCategoryCode extends Category.Standard.Code = Category.Standard.Code,
          TSubcategoryCode extends
            Subcategory.Standard.Code = Subcategory.Standard.Code,
        > = `${TCategoryCode}${typeof Id.SEPARATOR}${TSubcategoryCode}`
        export namespace Suffix {
          export const isSuffix: TypeChecker<Suffix> =
            TypeChecker.Factory.custom(
              (val: unknown): val is Suffix => {
                // Must be string
                if (!TypeChecker.isString(val)) {
                  return false
                }
                // Must include separator
                const idx = val.indexOf(SEPARATOR)
                if (idx === -1) {
                  return false
                }
                // Must start with category code & end with subcategory code
                const [categoryCode, subcategoryCode] = [
                  val.substring(0, idx),
                  val.substring(idx + 1),
                ]
                return (
                  Category.Standard.Code.isCode(categoryCode) &&
                  Subcategory.Standard.Code.isCode(subcategoryCode)
                )
              },
              { typeDescription: 'FaFunction.Subcategory.Standard.Id.Suffix' },
            )
        }

        export const SEPARATOR = '_'

        /**
         * Creates a {@link Subcategory.Standard.Id} from the names of a
         * {@link Category.Standard.Code} and {@link Subcategory.Standard.Code}.
         * Similar to {@link Id.create}, but avoids long references to
         * {@link Category.Standard.Code} and {@link Subcategory.Standard.Code}
         *
         * @see {@link Id.create}
         */
        export function of<
          KCat extends StandardCategoryCodeKey,
          KSubcat extends StandardSubcategoryCodeKey,
        >(
          categoryKey: KCat,
          subcategoryKey: KSubcat,
        ): Id<
          (typeof Category.Standard.Code)[KCat],
          (typeof Subcategory.Standard.Code)[KSubcat]
        > {
          return create(
            Category.Standard.Code[categoryKey],
            Subcategory.Standard.Code[subcategoryKey],
          )
        }

        /**
         * Creates a {@link Subcategory.Standard.Id} from a
         * {@link Category.Standard.Code} and {@link Subcategory.Standard.Code}
         */
        export function create<
          TCategoryCode extends Category.Standard.Code,
          TSubcategoryCode extends Subcategory.Standard.Code,
        >(
          categoryCode: TCategoryCode,
          subcategoryCode: TSubcategoryCode,
        ): Id<TCategoryCode, TSubcategoryCode> {
          return PartialId.Standard.create<
            Id.Suffix<TCategoryCode, TSubcategoryCode>
          >(`${categoryCode}${SEPARATOR}${subcategoryCode}`)
        }

        /**
         * Creates a {@link Subcategory.Standard.Id} from a
         * {@link Category.Standard.Code} and {@link Subcategory.Standard.Code}
         */
        export function parse<
          TCategoryCode extends Category.Standard.Code,
          TSubcategoryCode extends Subcategory.Standard.Code,
        >(
          id: Id<TCategoryCode, TSubcategoryCode>,
        ): {
          categoryCode: TCategoryCode
          subcategoryCode: TSubcategoryCode
        } {
          const suffix = PartialId.getSuffix(id)
          const [categoryCode, subcategoryCode] = suffix.split(
            SEPARATOR,
          ) as Split<typeof suffix, typeof SEPARATOR>
          return {
            categoryCode:
              categoryCode satisfies `${Category.Standard.Code}` as TCategoryCode,
            subcategoryCode:
              subcategoryCode satisfies `${Subcategory.Standard.Code}` as TSubcategoryCode,
          }
        }
      }

      /**
       * Code of a {@link Standard} {@link Subcategory}
       */
      export enum Code {
        UniversityWide = 'UW',
        EnvironmentalHealthAndSafety = 'EHS',
        Academic = 'ACAD',
        Room = 'ROOM',
        Building = 'BLDG',
        Campus = 'CAMPUS',
        Department = 'DEPT',
      }
      // Needs to be before namespace is defined
      const CODES: ReadonlySet<Code> = new Set(Object.values(Code) as Code[])
      export namespace Code {
        export const ALL: readonly Code[] = Array.from(CODES)
        export const isCode = TypeChecker.Factory.oneOf(CODES)
        export const assertIsCode: TypeAsserter<Code> =
          TypeAsserter.Factory.from(isCode)
      }
    }

    /**
     * {@link Subcategory} with the {@link Category.Id} for the {@link Category}
     * it is contained in
     */
    export interface WithCategoryId extends Subcategory {
      categoryId: Category.Id
    }
  }

  /**
   * Combination of a {@link Category} and potentially a {@link Subcategory}
   */
  export interface Categorization {
    // NOTE: a category should always be defined
    category: Category
    subcategory?: Subcategory
  }
  export namespace Categorization {
    export const isCategorization: TypeChecker<Categorization> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<Categorization>({
          category: Category.isCategory,
          subcategory: TypeChecker.Factory.optional(Subcategory.isSubcategory),
        }),
      ).usingAlias('FaFunction.Categorization')

    /**
     * IDs for potentially a {@link Category} and/or {@link Subcategory}
     */
    export interface Ids {
      // NOTE: a categoryId should always be defined
      categoryId: Category.Id
      subcategoryId?: Subcategory.Id
    }
    export namespace Ids {
      export const isIds: TypeChecker<Ids> = TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object<Ids>({
          categoryId: Category.Id.isId,
          subcategoryId: TypeChecker.Factory.optional(Subcategory.Id.isId),
        }),
      ).usingAlias('FaFunction.Categorization.Ids')

      export const assertIsIds: TypeAsserter<Ids> =
        TypeAsserter.Factory.from(isIds)

      /**
       * Produces a `string` that uniquely identifies these {@link Ids}
       */
      export function stringify(ids: Readonly<Ids>): string {
        return JSON.stringify({
          categoryId: ids.categoryId,
          subcategoryId: ids.subcategoryId,
        } satisfies Record<keyof Ids, string | undefined>)
      }

      /**
       * {@link Ids} from {@link Categorization}
       */
      export function from(identifier: undefined): undefined
      export function from(identifier: Identifiable): Ids
      export function from(
        identifier: Identifiable | undefined,
      ): Ids | undefined
      export function from(
        identifier: Identifiable | undefined,
      ): Ids | undefined {
        if (identifier === undefined) {
          return undefined
        }
        if (Ids.isIds(identifier)) {
          return identifier
        }
        return {
          categoryId: identifier.category?.id,
          subcategoryId: identifier.subcategory?.id,
        }
      }
    }

    /**
     * {@link Categorization} or just its {@link Ids}
     */
    export type Identifiable = Categorization | Ids
    export namespace Identifiable {
      export const isIdentifier: TypeChecker<Identifiable> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.union(isCategorization, Ids.isIds).usingAlias(
            'FaFunction.Categorization.Identifier',
          ),
        )

      /**
       * Converts the {@link Identifiable} to just {@link Ids} and then uses
       * {@link Ids.stringify}
       */
      export function stringify(identifier: Identifiable): string {
        return Ids.stringify(Ids.from(identifier))
      }
    }
  }
}

/**
 * @see {@link FaFunction.Category.Standard.Id.of}
 */
type StandardCategoryCodeKey = {
  [_K in keyof typeof FaFunction.Category.Standard.Code]: (typeof FaFunction.Category.Standard.Code)[_K] extends FaFunction.Category.Standard.Code
    ? _K
    : never
}[keyof typeof FaFunction.Category.Standard.Code]

/**
 * @see {@link FaFunction.Subcategory.Standard.Id.of}
 */
type StandardSubcategoryCodeKey = {
  [_K in keyof typeof FaFunction.Subcategory.Standard.Code]: (typeof FaFunction.Subcategory.Standard.Code)[_K] extends FaFunction.Subcategory.Standard.Code
    ? _K
    : never
}[keyof typeof FaFunction.Subcategory.Standard.Code]
